/**
 * Official-source citation + review byline block for FAQ hubs.
 * Every fee/timeline answer in the FAQ corpora must be traceable to a
 * government source; this block surfaces those sources on-page (E-E-A-T)
 * and states the last verification date.
 */

export type FaqSourceTopic =
  | "consular"
  | "translation"
  | "notary"
  | "police-clearance"
  | "business"
  | "visa"
  | "work-permit"
  | "company"
  | "marriage"
  | "property"
  | "inheritance"
  | "education"
  | "driving"
  | "medical"
  | "civil-registry"
  | "corporate-docs"
  | "interpreter"
  | "employment"
  | "poa"
  | "pension"
  | "tax"
  | "legal"
  | "relocation"
  | "insurance"
  | "vehicle"
  | "ip";

type Src = { label: string; url: string };

const SOURCES: Record<FaqSourceTopic, Src[]> = {
  consular: [
    { label: "กองสัญชาติและนิติกรณ์ กรมการกงสุล (consular.mfa.go.th)", url: "https://consular.mfa.go.th/" },
    { label: "กระทรวงการต่างประเทศ (mfa.go.th)", url: "https://www.mfa.go.th/" },
    { label: "HCCH — Apostille Section (hcch.net)", url: "https://www.hcch.net/en/instruments/conventions/specialised-sections/apostille" },
  ],
  translation: [
    { label: "กองสัญชาติและนิติกรณ์ กรมการกงสุล — การรับรองคำแปล", url: "https://consular.mfa.go.th/" },
    { label: "HCCH — Apostille Section", url: "https://www.hcch.net/en/instruments/conventions/specialised-sections/apostille" },
    { label: "NAATI (ออสเตรเลีย) — naati.com.au", url: "https://www.naati.com.au/" },
  ],
  notary: [
    { label: "สภาทนายความในพระบรมราชูปถัมภ์ (lawyerscouncil.or.th)", url: "https://www.lawyerscouncil.or.th/" },
    { label: "กรมการกงสุล — นิติกรณ์เอกสาร", url: "https://consular.mfa.go.th/" },
    { label: "HCCH — Apostille Section", url: "https://www.hcch.net/en/instruments/conventions/specialised-sections/apostille" },
  ],
  "police-clearance": [
    { label: "ศูนย์บริการออกหนังสือรับรองความประพฤติ สำนักงานตำรวจแห่งชาติ", url: "https://pcscenter.sb.police.go.th/" },
    { label: "สำนักงานตำรวจแห่งชาติ (royalthaipolice.go.th)", url: "https://www.royalthaipolice.go.th/" },
    { label: "กรมการกงสุล — รับรองเอกสารเพื่อใช้ต่างประเทศ", url: "https://consular.mfa.go.th/" },
  ],
  business: [
    { label: "กรมพัฒนาธุรกิจการค้า (dbd.go.th)", url: "https://www.dbd.go.th/" },
    { label: "สำนักงานคณะกรรมการส่งเสริมการลงทุน BOI (boi.go.th)", url: "https://www.boi.go.th/" },
    { label: "กรมสรรพากร (rd.go.th)", url: "https://www.rd.go.th/" },
  ],
  visa: [
    { label: "สำนักงานตรวจคนเข้าเมือง (immigration.go.th)", url: "https://www.immigration.go.th/" },
    { label: "ระบบ e-Visa กระทรวงการต่างประเทศ (thaievisa.go.th)", url: "https://www.thaievisa.go.th/" },
    { label: "กรมการจัดหางาน — ใบอนุญาตทำงาน (doe.go.th)", url: "https://www.doe.go.th/" },
    { label: "สำนักงานคณะกรรมการส่งเสริมการลงทุน BOI (boi.go.th)", url: "https://www.boi.go.th/" },
  ],
  "work-permit": [
    { label: "กรมการจัดหางาน กระทรวงแรงงาน (doe.go.th)", url: "https://www.doe.go.th/" },
    { label: "สำนักงานตรวจคนเข้าเมือง (immigration.go.th)", url: "https://www.immigration.go.th/" },
    { label: "สำนักงานคณะกรรมการส่งเสริมการลงทุน BOI (boi.go.th)", url: "https://www.boi.go.th/" },
    { label: "สำนักงานประกันสังคม (sso.go.th)", url: "https://www.sso.go.th/" },
    { label: "กรมสรรพากร (rd.go.th)", url: "https://www.rd.go.th/" },
  ],
  marriage: [
    { label: "กรมการปกครอง สำนักบริหารการทะเบียน (bora.dopa.go.th)", url: "https://www.bora.dopa.go.th/" },
    { label: "กรมการกงสุล — รับรองคำแปลและนิติกรณ์", url: "https://consular.mfa.go.th/" },
    { label: "สำนักงานตรวจคนเข้าเมือง (immigration.go.th)", url: "https://www.immigration.go.th/" },
    { label: "กรมที่ดิน (dol.go.th)", url: "https://www.dol.go.th/" },
  ],
  company: [
    { label: "กรมพัฒนาธุรกิจการค้า (dbd.go.th)", url: "https://www.dbd.go.th/" },
    { label: "สำนักงานคณะกรรมการส่งเสริมการลงทุน BOI (boi.go.th)", url: "https://www.boi.go.th/" },
    { label: "กรมสรรพากร (rd.go.th)", url: "https://www.rd.go.th/" },
    { label: "กรมการจัดหางาน — ใบอนุญาตทำงาน (doe.go.th)", url: "https://www.doe.go.th/" },
    { label: "กรมการกงสุล — นิติกรณ์เอกสารบริษัท", url: "https://consular.mfa.go.th/" },
  ],
  property: [
    { label: "กรมที่ดิน (dol.go.th)", url: "https://www.dol.go.th/" },
    { label: "กรมสรรพากร (rd.go.th)", url: "https://www.rd.go.th/" },
    { label: "ธนาคารแห่งประเทศไทย — การนำเงินตราต่างประเทศเข้าประเทศ (bot.or.th)", url: "https://www.bot.or.th/" },
    { label: "กรมพัฒนาธุรกิจการค้า (dbd.go.th)", url: "https://www.dbd.go.th/" },
    { label: "สำนักงานคณะกรรมการกฤษฎีกา — พ.ร.บ.อาคารชุด/ประมวลกฎหมายที่ดิน (krisdika.go.th)", url: "https://www.krisdika.go.th/" },
  ],
  education: [
    { label: "กองสัญชาติและนิติกรณ์ กรมการกงสุล — นิติกรณ์และรับรองคำแปล (consular.mfa.go.th)", url: "https://consular.mfa.go.th/" },
    { label: "กระทรวงศึกษาธิการ — การเทียบวุฒิการศึกษา (moe.go.th)", url: "https://www.moe.go.th/" },
    { label: "กระทรวงการอุดมศึกษา วิทยาศาสตร์ วิจัยและนวัตกรรม (mhesi.go.th)", url: "https://www.mhesi.go.th/" },
    { label: "คุรุสภา — ใบอนุญาตประกอบวิชาชีพครู (ksp.or.th)", url: "https://www.ksp.or.th/" },
    { label: "HCCH — Apostille Section (มีผลกับไทย 28 ก.พ. 2570)", url: "https://www.hcch.net/en/instruments/conventions/specialised-sections/apostille" },
  ],
  driving: [
    { label: "กรมการขนส่งทางบก — ใบอนุญาตขับรถและใบขับขี่สากล (dlt.go.th)", url: "https://www.dlt.go.th/" },
    { label: "พระราชบัญญัติรถยนต์ พ.ศ. 2522 และกฎหมายจราจรทางบก (krisdika.go.th)", url: "https://www.krisdika.go.th/" },
    { label: "กองสัญชาติและนิติกรณ์ กรมการกงสุล — รับรองคำแปลใบขับขี่ (consular.mfa.go.th)", url: "https://consular.mfa.go.th/" },
    { label: "UNECE — อนุสัญญาว่าด้วยการจราจรทางถนน เจนีวา 1949 / เวียนนา 1968", url: "https://unece.org/transport/road-safety" },
    { label: "HCCH — Apostille Section (มีผลกับไทย 28 ก.พ. 2570)", url: "https://www.hcch.net/en/instruments/conventions/specialised-sections/apostille" },
  ],
  inheritance: [
    { label: "สำนักงานคณะกรรมการกฤษฎีกา — ป.พ.พ. บรรพ 6 มรดก และ พ.ร.บ.ภาษีการรับมรดก 2558 (krisdika.go.th)", url: "https://www.krisdika.go.th/" },
    { label: "สำนักงานศาลยุติธรรม — คำร้องตั้งผู้จัดการมรดก (coj.go.th)", url: "https://www.coj.go.th/" },
    { label: "กรมที่ดิน — การโอนมรดกที่ดินและห้องชุด (dol.go.th)", url: "https://www.dol.go.th/" },
    { label: "กรมสรรพากร — ภาษีการรับมรดกและภาษีการให้ (rd.go.th)", url: "https://www.rd.go.th/" },
    { label: "กรมการกงสุล — นิติกรณ์และรับรองคำแปลเอกสารมรดก", url: "https://consular.mfa.go.th/" },
  ],
  medical: [
    { label: "แพทยสภา — แบบใบรับรองแพทย์และการประกอบวิชาชีพเวชกรรม (tmc.or.th)", url: "https://tmc.or.th/" },
    { label: "กรมสนับสนุนบริการสุขภาพ กระทรวงสาธารณสุข (hss.moph.go.th)", url: "https://hss.moph.go.th/" },
    { label: "สำนักงานตรวจคนเข้าเมือง — การขออยู่ต่อเพื่อรักษาพยาบาล (immigration.go.th)", url: "https://www.immigration.go.th/" },
    { label: "สำนักงาน คปภ. — ข้อพิพาทและกรมธรรม์ประกันสุขภาพ (oic.or.th)", url: "https://www.oic.or.th/" },
    { label: "WHO — International Health Regulations 2005 (ICVP)", url: "https://www.who.int/health-topics/international-health-regulations" },
    { label: "กรมการกงสุล — นิติกรณ์และรับรองคำแปลเอกสารแพทย์", url: "https://consular.mfa.go.th/" },
  ],
  "civil-registry": [
    { label: "สำนักบริหารการทะเบียน กรมการปกครอง (bora.dopa.go.th)", url: "https://www.bora.dopa.go.th/" },
    { label: "กรมการปกครอง กระทรวงมหาดไทย (dopa.go.th)", url: "https://www.dopa.go.th/" },
    { label: "สำนักงานคณะกรรมการกฤษฎีกา — พ.ร.บ.การทะเบียนราษฎร 2534 / พ.ร.บ.ชื่อบุคคล 2505 / พ.ร.บ.สัญชาติ 2508 (krisdika.go.th)", url: "https://www.krisdika.go.th/" },
    { label: "กรมการกงสุล — นิติกรณ์และรับรองคำแปลเอกสารทะเบียนราษฎร (consular.mfa.go.th)", url: "https://consular.mfa.go.th/" },
    { label: "HCCH — Apostille Section (มีผลกับไทย 28 ก.พ. 2570)", url: "https://www.hcch.net/en/instruments/conventions/specialised-sections/apostille" },
  ],
  employment: [
    { label: "กรมการกงสุล — นิติกรณ์และรับรองคำแปลเอกสารสมัครงาน (consular.mfa.go.th)", url: "https://consular.mfa.go.th/" },
    { label: "กรมการจัดหางาน — การไปทำงานต่างประเทศและการคุ้มครองคนหางาน (doe.go.th)", url: "https://www.doe.go.th/" },
    { label: "สำนักงาน ก.พ. — ทุนรัฐบาลและระเบียบการลาศึกษา (ocsc.go.th)", url: "https://www.ocsc.go.th/" },
    { label: "สป.อว. — การรับรองคุณวุฒิการศึกษา (mhesi.go.th)", url: "https://www.mhesi.go.th/" },
    { label: "แพทยสภา / สภาการพยาบาล / สภาวิศวกร / สภาสถาปนิก / คุรุสภา — Certificate of Good Standing", url: "https://tmc.or.th/" },
    { label: "NAATI — คำแปลที่ออสเตรเลียและนิวซีแลนด์ยอมรับ (naati.com.au)", url: "https://www.naati.com.au/" },
    { label: "WES — credential evaluation (wes.org)", url: "https://www.wes.org/" },
    { label: "ENIC-NARIC — เครือข่ายรับรองคุณวุฒิยุโรป (enic-naric.net)", url: "https://www.enic-naric.net/" },
    { label: "HCCH — Apostille Convention (มีผลกับไทย 28 ก.พ. 2570)", url: "https://www.hcch.net/en/instruments/conventions/specialised-sections/apostille" },
  ],
  pension: [
    { label: "กรมการกงสุล กองสัญชาติและนิติกรณ์ — รับรองคำแปลและนิติกรณ์เอกสาร (consular.mfa.go.th)", url: "https://consular.mfa.go.th/" },
    { label: "สำนักงานตรวจคนเข้าเมือง — หนังสือรับรองสถานที่พักอาศัยและการอยู่ต่อ (immigration.go.th)", url: "https://www.immigration.go.th/" },
    { label: "กรมการปกครอง สำนักบริหารการทะเบียน — ทะเบียนราษฎรและมรณบัตร (bora.dopa.go.th)", url: "https://www.bora.dopa.go.th/" },
    { label: "กรมการค้าต่างประเทศ กระทรวงพาณิชย์ — บัตรเดินทางนักธุรกิจเอเปก ABTC (dft.go.th)", url: "https://www.dft.go.th/" },
    { label: "APEC — Business Travel Card (apec.org)", url: "https://www.apec.org/groups/committee-on-trade-and-investment/business-mobility-group/abtc" },
    { label: "กรมสรรพากร — ภาษีเงินได้บุคคลธรรมดาและอนุสัญญาภาษีซ้อน (rd.go.th)", url: "https://www.rd.go.th/" },
    { label: "สภาทนายความในพระบรมราชูปถัมภ์ — ทนายความผู้ทำคำรับรองลายมือชื่อและเอกสาร (lawyerscouncil.or.th)", url: "https://www.lawyerscouncil.or.th/" },
    { label: "HCCH — Apostille Convention (มีผลกับไทย 28 ก.พ. 2570)", url: "https://www.hcch.net/en/instruments/conventions/specialised-sections/apostille" },
  ],
  poa: [
    { label: "ประมวลกฎหมายแพ่งและพาณิชย์ บรรพ 3 ลักษณะ 15 ตัวแทน มาตรา 797–828 (krisdika.go.th)", url: "https://www.krisdika.go.th/" },
    { label: "กรมที่ดิน — หนังสือมอบอำนาจที่ดิน แบบ ท.ด.21 (dol.go.th)", url: "https://www.dol.go.th/" },
    { label: "กรมสรรพากร — อากรแสตมป์ใบมอบอำนาจ (rd.go.th)", url: "https://www.rd.go.th/" },
    { label: "กรมพัฒนาธุรกิจการค้า — หนังสือรับรองนิติบุคคล (dbd.go.th)", url: "https://www.dbd.go.th/" },
    { label: "สภาทนายความในพระบรมราชูปถัมภ์ — ทนายความผู้ทำคำรับรองลายมือชื่อและเอกสาร (lawyerscouncil.or.th)", url: "https://www.lawyerscouncil.or.th/" },
    { label: "กรมการกงสุล กองสัญชาติและนิติกรณ์ (consular.mfa.go.th)", url: "https://consular.mfa.go.th/" },
    { label: "HCCH — Apostille Convention (มีผลกับไทย 28 ก.พ. 2570)", url: "https://www.hcch.net/en/instruments/conventions/specialised-sections/apostille" },
  ],
  interpreter: [
    { label: "สำนักงานคณะกรรมการกฤษฎีกา — ป.วิ.อ. มาตรา 13 และ ป.วิ.พ. มาตรา 46 (krisdika.go.th)", url: "https://www.krisdika.go.th/" },
    { label: "สำนักงานศาลยุติธรรม — ล่ามและอัตราค่าตอบแทนล่ามของศาล (coj.go.th)", url: "https://www.coj.go.th/" },
    { label: "สำนักบริหารการทะเบียน กรมการปกครอง — งานทะเบียนครอบครัว (bora.dopa.go.th)", url: "https://www.bora.dopa.go.th/" },
    { label: "สำนักงานตรวจคนเข้าเมือง (immigration.go.th)", url: "https://www.immigration.go.th/" },
    { label: "กรมที่ดิน — การจดทะเบียนสิทธิและนิติกรรม (dol.go.th)", url: "https://www.dol.go.th/" },
    { label: "สภาทนายความในพระบรมราชูปถัมภ์ — ทนายความผู้ทำคำรับรองลายมือชื่อและเอกสาร (lawyerscouncil.or.th)", url: "https://www.lawyerscouncil.or.th/" },
    { label: "กรมการกงสุล — นิติกรณ์และรับรองคำแปล (consular.mfa.go.th)", url: "https://consular.mfa.go.th/" },
    { label: "สำนักงานคณะกรรมการคุ้มครองข้อมูลส่วนบุคคล — PDPA (pdpc.or.th)", url: "https://www.pdpc.or.th/" },
  ],
  "corporate-docs": [
    { label: "กรมพัฒนาธุรกิจการค้า — หนังสือรับรองนิติบุคคลและงานจดทะเบียน (dbd.go.th)", url: "https://www.dbd.go.th/" },
    { label: "สำนักงานคณะกรรมการกฤษฎีกา — ป.พ.พ. บรรพ 3 และ พ.ร.บ.การประกอบธุรกิจของคนต่างด้าว 2542 (krisdika.go.th)", url: "https://www.krisdika.go.th/" },
    { label: "กรมสรรพากร — บัญชีอัตราอากรแสตมป์ ใบมอบอำนาจ (rd.go.th)", url: "https://www.rd.go.th/" },
    { label: "สภาทนายความในพระบรมราชูปถัมภ์ — ทนายความผู้ทำคำรับรองลายมือชื่อและเอกสาร (lawyerscouncil.or.th)", url: "https://www.lawyerscouncil.or.th/" },
    { label: "กรมการกงสุล — นิติกรณ์และรับรองคำแปลเอกสารนิติบุคคล (consular.mfa.go.th)", url: "https://consular.mfa.go.th/" },
    { label: "กรมทรัพย์สินทางปัญญา — เครื่องหมายการค้าและ Madrid Protocol (ipthailand.go.th)", url: "https://www.ipthailand.go.th/" },
    { label: "ธนาคารแห่งประเทศไทย — หลักเกณฑ์การแลกเปลี่ยนเงินและธุรกรรมข้ามประเทศ (bot.or.th)", url: "https://www.bot.or.th/" },
    { label: "HCCH — Apostille Convention (มีผลกับไทย 28 ก.พ. 2570)", url: "https://www.hcch.net/en/instruments/conventions/specialised-sections/apostille" },
  ],
  tax: [
    { label: "กรมสรรพากร — ประมวลรัษฎากรและแบบแสดงรายการภาษี (rd.go.th)", url: "https://www.rd.go.th/" },
    { label: "กรมสรรพากร — คำสั่งกรมสรรพากรที่ ป.161/2566 และ ป.162/2566 (เงินได้จากแหล่งต่างประเทศ)", url: "https://www.rd.go.th/" },
    { label: "กรมพัฒนาธุรกิจการค้า — พ.ร.บ.การบัญชี พ.ศ. 2543 และการยื่นงบการเงิน (dbd.go.th)", url: "https://www.dbd.go.th/" },
    { label: "สภาวิชาชีพบัญชีในพระบรมราชูปถัมภ์ — ผู้สอบบัญชีรับอนุญาต (tfac.or.th)", url: "https://www.tfac.or.th/" },
    { label: "สำนักงานประกันสังคม — การขึ้นทะเบียนนายจ้างและเงินสมทบ (sso.go.th)", url: "https://www.sso.go.th/" },
    { label: "ธนาคารแห่งประเทศไทย — หลักเกณฑ์การแลกเปลี่ยนเงินและธุรกรรมข้ามประเทศ (bot.or.th)", url: "https://www.bot.or.th/" },
    { label: "กรมการกงสุล — นิติกรณ์และรับรองคำแปลเอกสารภาษี (consular.mfa.go.th)", url: "https://consular.mfa.go.th/" },
    { label: "HCCH — Apostille Convention (มีผลกับไทย 28 ก.พ. 2570)", url: "https://www.hcch.net/en/instruments/conventions/specialised-sections/apostille" },
  ],
  legal: [
    { label: "สำนักงานคณะกรรมการกฤษฎีกา — ป.พ.พ. ป.วิ.พ. และ ป.วิ.อ. (krisdika.go.th)", url: "https://www.krisdika.go.th/" },
    { label: "กรมสวัสดิการและคุ้มครองแรงงาน — พ.ร.บ.คุ้มครองแรงงาน พ.ศ. 2541 (labour.go.th)", url: "https://www.labour.go.th/" },
    { label: "ศาลแรงงานกลาง — วิธีพิจารณาคดีแรงงาน (labourcourt.coj.go.th)", url: "https://labourcourt.coj.go.th/" },
    { label: "สำนักงานศาลยุติธรรม — ค่าธรรมเนียมศาลและการไกล่เกลี่ย (coj.go.th)", url: "https://www.coj.go.th/" },
    { label: "สถาบันอนุญาโตตุลาการ THAC (thac.or.th)", url: "https://thac.or.th/" },
    { label: "สถาบันอนุญาโตตุลาการ TAI สำนักงานศาลยุติธรรม (tai.coj.go.th)", url: "https://tai.coj.go.th/" },
    { label: "กรมพัฒนาธุรกิจการค้า — พ.ร.บ.การประกอบธุรกิจของคนต่างด้าว พ.ศ. 2542 (dbd.go.th)", url: "https://www.dbd.go.th/" },
    { label: "กรมบังคับคดี — การบังคับคดีตามคำพิพากษา (led.go.th)", url: "https://www.led.go.th/" },
    { label: "สภาทนายความในพระบรมราชูปถัมภ์ (lawyerscouncil.or.th)", url: "https://www.lawyerscouncil.or.th/" },
    { label: "กรมการกงสุล — นิติกรณ์และรับรองคำแปล (consular.mfa.go.th)", url: "https://consular.mfa.go.th/" },
    { label: "HCCH — Apostille Convention (มีผลกับไทย 28 ก.พ. 2570)", url: "https://www.hcch.net/en/instruments/conventions/specialised-sections/apostille" },
  ],
  insurance: [
    { label: "สำนักงาน คปภ. — กำกับธุรกิจประกันภัย สายด่วน 1186 (oic.or.th)", url: "https://www.oic.or.th/" },
    { label: "สมาคมประกันวินาศภัยไทย — ระบบกรมธรรม์วีซ่าพำนักระยะยาว (longstay.tgia.org)", url: "https://longstay.tgia.org/" },
    { label: "สำนักงานตรวจคนเข้าเมือง (immigration.go.th)", url: "https://www.immigration.go.th/" },
    { label: "BOI — วีซ่า LTR (ltr.boi.go.th)", url: "https://ltr.boi.go.th/" },
    { label: "สำนักงานประกันสังคม (sso.go.th)", url: "https://www.sso.go.th/" },
    { label: "กรมการกงสุล — นิติกรณ์และรับรองคำแปลเอกสารเคลม", url: "https://consular.mfa.go.th/" },
  ],
  ip: [
    { label: "กรมทรัพย์สินทางปัญญา กระทรวงพาณิชย์ (ipthailand.go.th)", url: "https://www.ipthailand.go.th/" },
    { label: "WIPO — Madrid System (wipo.int)", url: "https://www.wipo.int/madrid/en/" },
    { label: "WIPO — Patent Cooperation Treaty (PCT)", url: "https://www.wipo.int/pct/en/" },
    { label: "กรมพัฒนาธุรกิจการค้า — หนังสือรับรองนิติบุคคล (dbd.go.th)", url: "https://www.dbd.go.th/" },
    { label: "กรมการกงสุล — นิติกรณ์และรับรองคำแปล (consular.mfa.go.th)", url: "https://consular.mfa.go.th/" },
  ],
  vehicle: [
    { label: "กรมการขนส่งทางบก — ทะเบียนรถและใบอนุญาตขับรถ (dlt.go.th)", url: "https://www.dlt.go.th/" },
    { label: "กรมศุลกากร — การนำเข้า-ส่งออกรถ (customs.go.th)", url: "https://www.customs.go.th/" },
    { label: "กรมการค้าต่างประเทศ — ใบอนุญาตนำเข้ารถใช้แล้ว (dft.go.th)", url: "https://www.dft.go.th/" },
    { label: "สำนักงานตำรวจแห่งชาติ — ใบสั่งและค่าปรับจราจร (royalthaipolice.go.th)", url: "https://www.royalthaipolice.go.th/" },
    { label: "สำนักงาน คปภ. — ประกันภัยรถตาม พ.ร.บ. (oic.or.th)", url: "https://www.oic.or.th/" },
    { label: "กรมการกงสุล — รับรองคำแปลเอกสารยานยนต์ (consular.mfa.go.th)", url: "https://consular.mfa.go.th/" },
  ],
  relocation: [
    { label: "สำนักงานตรวจคนเข้าเมือง — แจ้งที่พักและรายงานตัว 90 วัน (immigration.go.th)", url: "https://www.immigration.go.th/" },
    { label: "กรมการปกครอง สำนักบริหารการทะเบียน — ทะเบียนบ้าน ท.ร.13 (bora.dopa.go.th)", url: "https://www.bora.dopa.go.th/" },
    { label: "กรมการขนส่งทางบก — ใบอนุญาตขับรถ (dlt.go.th)", url: "https://www.dlt.go.th/" },
    { label: "ธนาคารแห่งประเทศไทย — หลักเกณฑ์การรับลูกค้าและเงินตราต่างประเทศ (bot.or.th)", url: "https://www.bot.or.th/" },
    { label: "สำนักงานคณะกรรมการส่งเสริมการศึกษาเอกชน (opec.go.th)", url: "https://www.opec.go.th/" },
    { label: "สำนักงาน คปภ. — ธุรกิจประกันภัย (oic.or.th)", url: "https://www.oic.or.th/" },
    { label: "BOI — วีซ่าผู้พำนักระยะยาว LTR (ltr.boi.go.th)", url: "https://ltr.boi.go.th/" },
    { label: "กรมที่ดิน — การจดทะเบียนการเช่า (dol.go.th)", url: "https://www.dol.go.th/" },
  ],
};

/** Month/year the fee + timeline figures in the FAQ corpora were last verified. */
export const FAQ_VERIFIED_LABEL_TH = "กรกฎาคม 2569 (July 2026)";
export const FAQ_VERIFIED_LABEL_EN = "July 2026";

export function FaqSourceNote({
  topic,
  locale = "th",
}: {
  topic: FaqSourceTopic;
  locale?: "th" | "en";
}) {
  const sources = SOURCES[topic];
  const isTh = locale === "th";
  return (
    <section className="mt-12 rounded-lg border bg-muted/20 p-5 text-sm" aria-label={isTh ? "แหล่งข้อมูลราชการและวันที่ตรวจทาน" : "Official sources and review date"}>
      <h2 className="text-base font-semibold mb-2">
        {isTh ? "แหล่งข้อมูลราชการที่ใช้อ้างอิง" : "Official sources referenced"}
      </h2>
      <ul className="mb-3 space-y-1">
        {sources.map((s) => (
          <li key={s.url}>
            •{" "}
            <a href={s.url} target="_blank" rel="noopener nofollow" className="text-primary underline">
              {s.label}
            </a>
          </li>
        ))}
      </ul>
      <p className="text-muted-foreground">
        {isTh ? (
          <>
            ค่าธรรมเนียมราชการและกรอบเวลาในหน้านี้ตรวจทานล่าสุด{" "}
            <strong>{FAQ_VERIFIED_LABEL_TH}</strong> โดยทีมทนายความผู้ทำคำรับรองลายมือชื่อและเอกสาร
            (ขึ้นทะเบียนสภาทนายความในพระบรมราชูปถัมภ์) ตัวเลขค่าธรรมเนียมและระยะเวลาเป็นค่าประมาณตามประกาศของหน่วยงาน
            ซึ่งอาจเปลี่ยนแปลงได้ และระยะเวลาจริงขึ้นกับคิวของหน่วยงานปลายทาง
            โปรดตรวจสอบกับหน่วยงานอีกครั้งก่อนดำเนินการ
          </>
        ) : (
          <>
            Government fees and processing times on this page were last verified in{" "}
            <strong>{FAQ_VERIFIED_LABEL_EN}</strong> by our Notarial Services Attorneys registered with the
            Lawyers Council of Thailand. Figures follow published agency schedules, may change without notice,
            and actual turnaround depends on each authority's queue. Please reconfirm with the issuing
            authority before you file.
          </>
        )}
      </p>
    </section>
  );
}